import { ROUTES } from "@/constants/routes";
import type { PaneLocation } from "@/types/pane-routing";
import type { InboxItem } from "@cohesive/api-client";
import { matchPath } from "react-router-dom";

/**
 * Check if an inbox item is currently being viewed in the right pane
 */
export function checkIfInboxItemIsCurrentlyViewed(
  itemId: string,
  flowSessionId: string | undefined,
  rightPaneLocation: PaneLocation | null,
): boolean {
  if (!rightPaneLocation?.pathname) return false;

  const pathname = rightPaneLocation.pathname;

  // Check if viewing this specific inbox item
  const inboxItemMatch = matchPath(
    { path: ROUTES.INBOX.ITEM, end: true },
    pathname,
  );
  if (inboxItemMatch?.params.inboxItemId === itemId) return true;

  // Check if viewing the session associated with this inbox item
  if (flowSessionId) {
    const sessionMatch = matchPath(
      { path: ROUTES.SESSIONS.SESSION, end: true },
      pathname,
    );
    if (sessionMatch?.params.sessionId === flowSessionId) return true;
  }

  return false;
}

/**
 * Mark inbox items as read if they are currently being viewed
 * Returns transformed items and IDs that need server updates
 */
export function markViewedItemsAsRead(
  items: InboxItem[],
  rightPaneLocation: PaneLocation | null,
): { items: InboxItem[]; idsToUpdate: string[] } {
  const idsToUpdate: string[] = [];

  const transformedItems = items.map((item) => {
    const isViewed = checkIfInboxItemIsCurrentlyViewed(
      item.id,
      item.flow_session_id,
      rightPaneLocation,
    );

    // If the item is currently viewed and not marked as read, update it
    if (isViewed && !item.is_read) {
      idsToUpdate.push(item.id);
      return {
        ...item,
        is_read: true,
      };
    }

    return item;
  });

  return { items: transformedItems, idsToUpdate };
}
